import { Component, Input, forwardRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { TranslatePipe } from '../../../pipes/translate.pipe';

/**
 * 子表單關聯元件
 *
 * 用途：建立主表單與子表單之間的關聯關係
 *
 * 主要功能：
 * 1. 在表單建立/編輯階段配置子表單關聯
 * 2. 在動態表單的新增/編輯模式下隱藏但參與表單提交
 * 3. 在 preview 模式下可以顯示預覽效果
 * 4. 在資料列表中顯示關聯子表單的資料筆數
 *
 * 重要特性：
 * - 實作 ControlValueAccessor 介面，完整支援 Angular reactive forms
 * - 根據 preview 參數控制顯示/隱藏
 * - 自動將設定的 formId 填入表單控制項的值
 * - 支援多語言顯示
 */
@Component({
  selector: 'app-form-subform-relation',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  template: `
    <div class="form-section">
      <label [attr.for]="id" [class.required-field]="required">{{ label }}</label>
      <div class="subform-relation-info">
        <i class="fas fa-link"></i>
        <span>{{ 'form.subform_relation.linked_to' | translate }}: {{ formId || ('form.subform_relation.not_set' | translate) }}</span>
      </div>
      <input
        type="hidden"
        [id]="id"
        [name]="name"
        [value]="value"
      />
    </div>
  `,
  styles: [`
    .form-section {
      margin-bottom: 16px;
    }

    .form-section label {
      display: block;
      margin-bottom: 4px;
      color: #484949;
      font-weight: 500;
      font-size: 14px;
    }

    .form-section label.required-field::after {
      content: ' *';
      color: #d9534f;
    }

    .subform-relation-info {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px;
      background-color: #ffecc5;
      border: 1px solid #ddd;
      border-radius: 4px;
      margin-top: 4px;
    }

    .subform-relation-info i {
      color: #f5820d;
      font-size: 16px;
    }

    .subform-relation-info span {
      color: #484949;
      font-size: 14px;
      font-weight: 500;
    }
  `],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormSubformRelationComponent),
      multi: true,
    },
  ]
})
export class FormSubformRelationComponent implements ControlValueAccessor, OnInit {
  /** 欄位標籤 */
  @Input() label: string = '子表單關聯';

  /** 欄位ID */
  @Input() id: string = 'subform-relation';

  /** 欄位名稱 */
  @Input() name: string = 'subform-relation';

  /** 關聯的子表單ID */
  @Input() formId: string = '';

  /** 輸入提示文字 */
  @Input() placeholder: string = '';

  /** 是否必填 */
  @Input() required: boolean = false;

  /** 是否為預覽模式 - 控制元件顯示/隱藏 */
  @Input() preview: boolean = false;

  /** 內部值 */
  value: string = '';

  /** onChange 回調函數 */
  onChange = (value: any) => {};

  /** onTouched 回調函數 */
  onTouched = () => {};

  ngOnInit() {
    // 初始化時設定表單ID為值
    if (this.formId) {
      this.value = this.formId;
      // 確保立即觸發 onChange 事件
      setTimeout(() => {
        this.onChange(this.value);
      }, 0);
    }
  }

  // ControlValueAccessor 實作
  writeValue(value: any): void {
    this.value = value || this.formId || '';
    // 如果收到的值是空的但有 formId，則使用 formId
    if (!value && this.formId) {
      this.value = this.formId;
      // 通知表單控制項更新值
      setTimeout(() => {
        this.onChange(this.value);
      }, 0);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // 可在此處添加禁用邏輯
  }
}
